import { isArray } from "lodash";
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useReactFlow } from "reactflow";
import { Dimmer, Loader } from "semantic-ui-react";
import { useGetFacilityWholeList, useGetTransportTypesList } from "../../../../../api/cultivations";
import {
  useDeleteProcessGateProcessData,
  useGetProcessGateProcessDataById,
  usePostProcessGateProcessData,
  usePutProcessGateProcessData
} from "../../../../../api/process";
import { useGetChemicalTypes } from "../../../../../api/static-data";
import ConfirmModal from "../../../../../components/confirmViewModal/ConfirmModal";
import { DrawingContext } from "../../../../../components/drawing-tool/DrawingTool";
import { isNodeLabelExists } from "../../../../../components/drawing-tool/utils";
import NoteAndDocument from "../../../../../components/noteAndDocument/NoteAndDocument";
import ProcessingDetails from "../../../../../components/processingDetails/ProcessingDetails";
import { WasteManagementContent } from "../../../../../components/wasteManagment/WasteManagementModal";
import { dataTypeOptions } from "../../../../../config/constants";
import { FacilityType } from "../../../../../config/enums/FacilityType.enum";
import { IngredientDataTypesOption } from "../../../../../config/enums/IngredientType.enum";
import { MEASUREMENT_TYPES } from "../../../../../config/enums/MeasurementTypes";
import { NodeLabelExistError } from "../../../../../config/enums/NodeLabelError.enum";
import { AccordionTypes } from "../../../../../helpers/AccordionHelper";
import { errorView, successMessage } from "../../../../../helpers/ErrorHandler";
import { removeNewDrawingItemStatus, saveNewDrawingItem } from "../../../../../utils/cacheStorage";
import { ChemicalContent } from "../../../../cultivation/processing/ChemicalContent";
import AddIngredientModal from "../../../../cultivation/processing/IngredientContentModal";
import RecipeContent from "../../../../cultivation/processing/RecipeModal";
import SipContentContent from "../../../../cultivation/processing/SipContentModal";
import ManageFacilityData from "../../../../facility/ManageFacilityData";

interface SaveDocumentData {
  saveDocumentAndNote(id: any): void;
  deleteComment(): void;
}

interface ProcessFormData {
  processingName: string;
  processingType: number;
  facilityName: string;
  machineModelNo: string;
  capacity: number;
  capacityUnit: number;
  input: number;
  inputUnit: number;
  output: number;
  outputUnit: number;
  sideStream: number;
  sideStreamUnit: number;
  heat: number;
  heatUnit: number;
  electricity: number;
  electricityUnit: number;
  water: number;
  waterUnit: number;
  citricAcid?: number;
  dataType?: number;
  heatSource?: any[];
}

interface ModalData {
  processGateId?: string;
  processGateFacilityId?: string;
  ownerId?: string;
  sourceId?: string;
  nodeItem?: any;
  ingredientId?: string;
  supplierId?: string;
}

export const ProcessGateProcessingBase = ({ modalData }: { modalData: ModalData }) => {
  const {
    processGateId,
    processGateFacilityId,
    ownerId,
    sourceId,
    nodeItem,
    ingredientId,
    supplierId
  } = modalData;

  const { deleteElements, getEdges } = useReactFlow();

  const {
    nodeItemId,
    chartEdges,
    chartNodes,
    setNodeItem,
    setChartNodes,
    lockStatus,
    deleteLockStatus: rawDeleteLockStatus,
    saveDrawing
  } = useContext(DrawingContext);
  const deleteLockStatus = rawDeleteLockStatus ?? lockStatus;

  const [processId, setProcessId] = useState<string>(nodeItem?.data?.reference?.processId);
  const ref = useRef<SaveDocumentData>(null);
  const [viewIngredient, setViewIngredient] = useState(false);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const {
    data: facilityWholeList,
    isSuccess: isFacilitySuccess,
    refetch: refetchFacilityList
  } = useGetFacilityWholeList(FacilityType.FACTORY);
  const { mutate: postFarmGateProcessData, isLoading: isCreateProcessLoading } =
    usePostProcessGateProcessData();
  const { mutate: putFarmGateProcessData, isLoading: isUpdateProcessLoading } =
    usePutProcessGateProcessData();
  const { mutate: deleteProcessGateProcessData, isLoading: isDeleteProcessLoading } =
    useDeleteProcessGateProcessData();

  const { data, isLoading, refetch } = useGetProcessGateProcessDataById(processId);
  const [accordionType, setAccordionType] = useState<AccordionTypes | null>(null);
  const [cipSipData, setCipSipData] = useState<any>([]);
  const [visibleFacilityManager, setVisibleFacilityManager] = useState<boolean>(false);

  const connectedEdges = getEdges().filter(
    edge => edge.source === nodeItemId || edge.target === nodeItemId
  );

  const { data: chemicalDataType } = useGetChemicalTypes();

  // Sort chemicalDataType by chemical_name
  const sortedChemicalDataType = useMemo(() => {
    return chemicalDataType?.sort((a: any, b: any) => a.text.localeCompare(b.text));
  }, [chemicalDataType]);

  const existingProcessingName = useMemo(() => {
    return data?.processingName;
  }, [data?.processingName]);

  useEffect(() => {
    if (processId) {
      refetch();
    }
  }, [processId, refetch]);

  const values = useMemo(() => {
    setCipSipData(data?.cipSip);
    return {
      processingName: data?.processingName,
      processingType: data?.processingType?.toString(),
      facilityName: data?.facilityId,
      dataType: data?.dataType || IngredientDataTypesOption.SPECIFIC,
      machineModelNo: data?.machineModelNo,
      capacity: data?.capacity || "",
      input: data?.input || "",
      output: data?.output || "",
      sideStream: data?.sideStream || "",
      heat: data?.heat || "",
      electricity: data?.electricity || "",
      water: data?.water || "",
      citricAcid: data?.citricAcid,
      recipeId: data?.recipeId,
      heatAmount: data?.heatAmount || 0,
      heatSource: data?.heatSource || [],
      capacityUnit: data?.capacityUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG.toString(),
      inputUnit: data?.inputUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG.toString(),
      outputUnit: data?.outputUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG.toString(),
      sideStreamUnit: data?.sideStreamUnit?.toString() ?? MEASUREMENT_TYPES.WEIGHT_KG.toString(),
      heatUnit: data?.heatUnit?.toString() ?? MEASUREMENT_TYPES.ENERGY_MJ_KG.toString(),
      electricityUnit:
        data?.electricityUnit?.toString() ?? MEASUREMENT_TYPES.POWER_kWh_KG.toString(),
      waterUnit: data?.waterUnit?.toString() ?? MEASUREMENT_TYPES.VOLUME_m3_PER_KG.toString()
    };
  }, [data]);

  const form = useForm({
    mode: "all",
    defaultValues: values,
    values: values
  });

  const {
    handleSubmit,
    setValue,
    getValues,
    formState: { errors, isDirty, isValid },
    control,
    setFocus,
    setError,
    clearErrors
  } = form;

  const chemicalData = data?.chemical || [];
  const wasteManagement = data?.wasteManagements || [];
  const facilityId = useWatch({ control, name: "facilityName" });
  const dataType = useWatch({ control, name: "dataType" });

  const effectiveDataType = dataType || IngredientDataTypesOption.SPECIFIC;

  const hideContentGenericStatus = useMemo(() => {
    return effectiveDataType === IngredientDataTypesOption.SPECIFIC;
  }, [effectiveDataType]);

  const { data: TransportTypeList } = useGetTransportTypesList();

  useEffect(() => {
    if (isFacilitySuccess) {
      setValue("facilityName", data?.facilityId || processGateFacilityId);
    }
  }, [isFacilitySuccess, data, processGateFacilityId, setValue]);

  const handleAccordion = (requestType: AccordionTypes) => {
    // Toggle accordion: close if same, open if different
    setAccordionType(accordionType === requestType ? null : requestType);
  };

  const handleChemicalAccordionStatus = useCallback(
    (activeStatus: boolean, requestType: any) => {
      const isChemicalsActive = accordionType === AccordionTypes.CHEMICALS;
      return isChemicalsActive;
    },
    [accordionType]
  );

  const handleChemicalValidation = () => {
    // Allow chemical accordion to open without validation
    return true;
  };

  const handleFormSubmit = (data: ProcessFormData) => {
    onSubmit(data, undefined, () => {
      setAccordionType(AccordionTypes.RECIPE);
    });
  };

  const updateNodeData = async (data: any, checkIsValidCipSip: boolean): Promise<void> => {
    try {
      const updatedNode = {
        ...nodeItem,
        data: {
          ...nodeItem?.data,
          label: data?.processingName,
          reference: {
            processId: data?._id
          }
        }
      };
      await ref?.current?.saveDocumentAndNote(updatedNode?.data?.reference?.processId);
      const updatedChartNodes = chartNodes.map(n => {
        if (n.id === nodeItem?.id) {
          return updatedNode;
        }
        return n;
      });
      if (checkIsValidCipSip) {
        successMessage("Process updated CIP / SIP successfully");
        return;
      }
      setChartNodes(updatedChartNodes);
      setNodeItem(updatedNode);
      saveDrawing(chartEdges, updatedChartNodes);
      successMessage(processId ? "Process updated successfully" : "Process created successfully");
    } catch (error) {
      errorView(error);
    }
  };

  const onSubmit = (data: ProcessFormData, cipSip?: any, callBack?: () => void): void => {
    const processingNameChanged = getValues("processingName");

    if (
      existingProcessingName !== processingNameChanged &&
      isNodeLabelExists(chartNodes as any, processingNameChanged)
    ) {
      clearErrors("processingName");
      setFocus("processingName");
      setError("processingName", { type: "manual", message: NodeLabelExistError.PROCESSING });
      return;
    }

    let checkIsValidCipSip = isArray(cipSip);

    const payload = {
      processingName: data?.processingName,
      ownerId: ownerId,
      sourceId: sourceId,
      ingredientId: ingredientId,
      supplierId: supplierId,
      processNumber: 1,
      facilityId: data?.facilityName,
      processingIngredient: [],
      cipSip: checkIsValidCipSip ? cipSip : cipSipData,
      processingType: data?.processingType,
      machineModelNo: data?.machineModelNo,
      capacity: data?.capacity || 0,
      input: data?.input || 0,
      output: data?.output || 0,
      sideStream: data?.sideStream || 0,
      heat: data?.heat || 0,
      electricity: data?.electricity || 0,
      water: data?.water || 0,
      citricAcid: data?.citricAcid || 0,
      processingStatus: "ACTIVE",
      heatSource: data?.heatSource || [],
      processGateId: processGateId,
      processId: processId,
      dataType: data?.dataType,
      capacityUnit: data?.capacityUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      inputUnit: data?.inputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      outputUnit: data?.outputUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      sideStreamUnit: data?.sideStreamUnit ?? MEASUREMENT_TYPES.WEIGHT_KG,
      heatUnit: data?.heatUnit ?? MEASUREMENT_TYPES.ENERGY_MJ_KG,
      electricityUnit: data?.electricityUnit ?? MEASUREMENT_TYPES.POWER_kWh_KG,
      waterUnit: data?.waterUnit ?? MEASUREMENT_TYPES.VOLUME_m3_PER_KG
    };

    if (processId) {
      putFarmGateProcessData(payload, {
        onSuccess: (data: any) => {
          removeNewDrawingItemStatus();
          refetch();
          updateNodeData(data, checkIsValidCipSip);
          if (callBack) callBack();
        }
      });
    } else {
      postFarmGateProcessData(payload, {
        onSuccess: (data: any) => {
          setProcessId(data?._id);
          saveNewDrawingItem("created");
          updateNodeData(data, checkIsValidCipSip);
          if (callBack) callBack();
        }
      });
    }
  };

  const handleComponentDataSave = async (processId: string): Promise<void> => {
    try {
      await ref?.current?.saveDocumentAndNote(processId);
    } catch (error) {
      errorView(error);
    }
  };

  const refetchWasteData = () => {
    refetch();
  };

  const onNodesDelete = () => {
    ref?.current?.deleteComment();
    deleteElements({ nodes: [{ id: nodeItem.id }], edges: connectedEdges });
    setNodeItem(null);
  };

  if (
    (processId && isLoading) ||
    isCreateProcessLoading ||
    isUpdateProcessLoading ||
    isDeleteProcessLoading
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const updateCipSipData = (data: any): void => {
    setCipSipData(data);
    const currentData = getValues();
    onSubmit(currentData, data);
  };

  const handleDeleteSuccess = () => {
    onNodesDelete();
    successMessage("Process deleted successfully");
    setVisibleDeleteModal(false);
  };

  const handleDeleteProcessGateProcess = () => {
    const dataToDelete = {
      processGateId: processGateId,
      processId: processId
    };

    if (processId) {
      deleteProcessGateProcessData(dataToDelete, {
        onSuccess: handleDeleteSuccess
      });
      return;
    }
    handleDeleteSuccess();
  };

  return (
    <>
      <div className="tw-w-full tw-p-2">
        <div className="userBankDetailsMain">
          <FormProvider {...form}>
            <ProcessingDetails
              control={control}
              setValue={setValue}
              facilityWholeList={facilityWholeList}
              dataTypeOptions={dataTypeOptions}
              setVisibleFacilityDetailsModal={() => setVisibleFacilityManager(true)}
              refetchFacilityList={refetchFacilityList}
              setViewIngredient={setViewIngredient}
              data={data}
              accordionType={accordionType}
              handleClick={handleAccordion}
              lockStatus={lockStatus}
              deleteLockStatus={deleteLockStatus}
              handleSubmit={handleSubmit}
              handleFormSubmit={handleFormSubmit}
              isValid={isValid}
              setVisibleDeleteModal={setVisibleDeleteModal}
            />

            <div className="tw-space-y-0 tw-mt-2">
              <div className="tw-w-full">
                <RecipeContent
                  processId={processId}
                  nodeItem={nodeItem}
                  recipeId={data?.recipeId}
                  accordionType={accordionType}
                  refetchProcess={refetch}
                  type="process-gate"
                  handleAccordion={handleAccordion}
                  onRecipeSave={handleComponentDataSave}
                  lockStatus={lockStatus}
                />
              </div>
              {hideContentGenericStatus ? (
                <>
                  <div className="tw-w-full tw-pt-0">
                    <SipContentContent
                      getSipData={sipData => {
                        updateCipSipData(sipData);
                      }}
                      accordionType={accordionType}
                      currentCipSipData={data?.cipSip}
                      handleAccordion={handleAccordion}
                      lockStatus={lockStatus}
                    />
                  </div>
                  <div className="tw-w-full tw-pt-0">
                    <ChemicalContent
                      chemicalDataType={sortedChemicalDataType}
                      chemicalData={chemicalData}
                      processId={processId}
                      TransportTypeList={TransportTypeList}
                      refetchChemical={refetch}
                      handleOpenValidation={handleChemicalValidation}
                      type="process-gate"
                      currentAccordionStatus={handleChemicalAccordionStatus}
                      onChemicalSave={handleComponentDataSave}
                      lockStatus={lockStatus}
                    />
                  </div>
                  <div className="tw-w-full tw-pt-0 tw-mb-2">
                    <WasteManagementContent
                      wasteManagement={wasteManagement}
                      processId={processId}
                      accordionType={accordionType}
                      sideStreamValue={values?.sideStream}
                      refetchWasteData={refetchWasteData}
                      type="process-gate"
                      handleAccordion={handleAccordion}
                      onWasteManagementSave={handleComponentDataSave}
                      lockStatus={lockStatus}
                      parentControl={control}
                    />
                  </div>
                </>
              ) : null}
            </div>
            <NoteAndDocument
              parentId={processId}
              ref={ref}
              showSaveButton={true}
              isDisabled={lockStatus}
            />
            {visibleFacilityManager && (
              <ManageFacilityData
                facilityId={facilityId}
                closeManager={() => setVisibleFacilityManager(false)}
              />
            )}
          </FormProvider>
        </div>
        <AddIngredientModal
          visibleModal={viewIngredient}
          setVisibleModal={() => {
            setViewIngredient(false);
          }}
        />
      </div>

      {/* Modal explicitly mounted to document.body to bypass Sheet portal */}
      {visibleDeleteModal && (
        <ConfirmModal
          viewModal={visibleDeleteModal}
          closeModal={() => setVisibleDeleteModal(false)}
          cancel={() => setVisibleDeleteModal(false)}
          approve={() => handleDeleteProcessGateProcess()}
          title={`Delete ${data?.processingName || ""} Process`}
          subTitle={`Are you sure you want to remove the ${
            data?.processingName || ""
          } process gate process? Please consider you cannot recover these data after remove`}
          style={{ zIndex: 10001 }}
          className="delete-confirmation-modal"
          mountNode={document.body}
        />
      )}
    </>
  );
};
